/**
 * Right-anchored side panel that hosts a server-rendered create/edit form over the
 * page, which stays visible behind a scrim.
 *
 * Any link carrying `data-side-panel` opens its href in the panel instead of
 * navigating; the plain href still works without JS. The form partial is fetched
 * with `?panel=1` and `X-Side-Panel: 1`, and must render a `[data-side-panel-form]`
 * (the `x-side-panel-form` Blade component). A response without one — a redirect
 * to a full page, an error page — falls back to navigating to the link.
 *
 * Saving posts the form as JSON. A 422 renders its errors inside the panel; a
 * success closes it and dispatches `side-panel:saved` on document, which the page
 * listens for to refresh its table and totals.
 *
 * Closing with edited fields (✕, Cancel, scrim, Esc), or following a link out of
 * the panel, asks before discarding them.
 *
 * `side-panel:mounted` and `side-panel:unmount` are dispatched on document with the
 * form, for a component to mount into it (an uploader) and come down with it.
 */

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let shell = null;
let opener = null;
let dirty = false;
let saving = false;
// Where to go once edits are discarded; null means just close the panel.
let pendingHref = null;
// Bumped on every open and close, so a form that arrives late lands nowhere.
let request = 0;

const t = (text) => (typeof window.$t === 'function' ? window.$t(text) : text);

function el(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
}

function buildShell() {
    const scrim = el('<div class="tw-fixed tw-inset-0 tw-z-90 tw-hidden tw-bg-black/40" data-side-panel-scrim aria-hidden="true"></div>');

    const panel = el(`
        <aside role="dialog" aria-modal="true" aria-labelledby="side-panel-title"
               class="tw-fixed tw-inset-y-0 tw-end-0 tw-z-100 tw-hidden tw-h-full tw-w-full tw-max-w-md tw-flex-col tw-bg-white tw-shadow-primary"
               data-side-panel-shell>
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-grey tw-px-6 tw-py-5">
                <div class="tw-min-w-0">
                    <h2 id="side-panel-title" class="tw-mb-0 tw-text-lg tw-font-bold tw-text-charcoal"></h2>
                    <p class="tw-mb-0 tw-mt-1 tw-hidden tw-text-sm tw-text-charcoal-60" data-side-panel-subtitle></p>
                </div>
                <button type="button" class="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-xl tw-leading-none tw-text-charcoal-50 hover:tw-bg-charcoal-10 hover:tw-text-charcoal"
                        data-side-panel-close></button>
            </div>
            <div class="tw-relative tw-min-h-0 tw-flex-1" data-side-panel-body></div>
            <div class="tw-absolute tw-inset-0 tw-z-10 tw-hidden tw-items-center tw-justify-center tw-bg-black/40 tw-p-6"
                 role="alertdialog" aria-modal="true" aria-labelledby="side-panel-confirm-title" aria-describedby="side-panel-confirm-text"
                 data-side-panel-confirm>
                <div class="tw-w-full tw-rounded-lg tw-bg-white tw-p-5 tw-shadow-primary">
                    <h3 id="side-panel-confirm-title" class="tw-mb-2 tw-text-lg tw-font-semibold tw-text-charcoal"></h3>
                    <p id="side-panel-confirm-text" class="tw-mb-4 tw-text-sm tw-text-charcoal-75"></p>
                    <div class="tw-flex tw-justify-end tw-gap-3">
                        <button type="button" class="button secondary" data-side-panel-keep></button>
                        <button type="button" class="button danger" data-side-panel-discard></button>
                    </div>
                </div>
            </div>
        </aside>
    `);

    const closeButton = panel.querySelector('[data-side-panel-close]');
    closeButton.textContent = '✕';
    closeButton.setAttribute('aria-label', t('Close'));
    panel.querySelector('#side-panel-confirm-title').textContent = t('Discard changes?');
    panel.querySelector('#side-panel-confirm-text').textContent = t('You have unsaved changes in this panel. Closing it now will discard them.');
    panel.querySelector('[data-side-panel-keep]').textContent = t('Keep editing');
    panel.querySelector('[data-side-panel-discard]').textContent = t('Discard');

    document.body.appendChild(scrim);
    document.body.appendChild(panel);

    scrim.addEventListener('click', requestClose);
    panel.addEventListener('click', (event) => {
        if (event.target.closest('[data-side-panel-close]')) {
            event.preventDefault();
            requestClose();
        } else if (event.target.closest('[data-side-panel-keep]')) {
            hideConfirm();
        } else if (event.target.closest('[data-side-panel-discard]')) {
            const href = pendingHref;
            close();
            if (href) window.location.href = href;
        } else if (event.target.closest('[data-side-panel-note-toggle]')) {
            toggleNote(event.target.closest('[data-side-panel-note-toggle]'));
        } else {
            guardLink(event);
        }
    });
    panel.addEventListener('keydown', trapFocus);

    return {
        scrim,
        panel,
        title: panel.querySelector('#side-panel-title'),
        subtitle: panel.querySelector('[data-side-panel-subtitle]'),
        body: panel.querySelector('[data-side-panel-body]'),
        confirm: panel.querySelector('[data-side-panel-confirm]'),
    };
}

function isOpen() {
    return !!shell && !shell.panel.classList.contains('tw-hidden');
}

function confirmShowing() {
    return !!shell && !shell.confirm.classList.contains('tw-hidden');
}

function show() {
    shell.scrim.classList.remove('tw-hidden');
    shell.panel.classList.remove('tw-hidden');
    shell.panel.classList.add('tw-flex');
    document.body.classList.add('tw-overflow-hidden');
}

function showConfirm() {
    shell.confirm.classList.remove('tw-hidden');
    shell.confirm.classList.add('tw-flex');
    shell.confirm.querySelector('[data-side-panel-keep]').focus();
}

function hideConfirm() {
    pendingHref = null;
    shell.confirm.classList.add('tw-hidden');
    shell.confirm.classList.remove('tw-flex');
    focusFirstField();
}

/** A link out of the panel (Open full page, "create one") asks first when there are edits. */
function guardLink(event) {
    const link = event.target.closest('a[href]');
    if (!link || !shell.body.contains(link) || !dirty) return;
    if (link.target === '_blank' || event.ctrlKey || event.metaKey || event.shiftKey) return;

    event.preventDefault();
    event.stopPropagation();
    pendingHref = link.href;
    showConfirm();
}

function toggleNote(button) {
    const more = button.closest('[data-side-panel-note]')?.querySelector('[data-side-panel-note-more]');
    if (!more) return;

    const expanded = more.classList.toggle('tw-hidden') === false;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
}

function focusFirstField() {
    const field = shell.body.querySelector('input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled])');
    (field || shell.panel.querySelector('[data-side-panel-close]')).focus();
}

function trapFocus(event) {
    if (event.key !== 'Tab') return;

    const scope = confirmShowing() ? shell.confirm : shell.panel;
    const focusable = Array.from(scope.querySelectorAll(FOCUSABLE)).filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}

function requestClose() {
    if (saving) return;

    if (dirty) {
        showConfirm();

        return;
    }

    close();
}

function close() {
    if (!shell) return;

    request += 1;
    pendingHref = null;
    shell.scrim.classList.add('tw-hidden');
    shell.panel.classList.add('tw-hidden');
    shell.panel.classList.remove('tw-flex');
    shell.confirm.classList.add('tw-hidden');
    shell.confirm.classList.remove('tw-flex');
    const form = shell.body.querySelector('[data-side-panel-form]');
    if (form) {
        document.dispatchEvent(new CustomEvent('side-panel:unmount', { detail: { form } }));
    }
    shell.body.innerHTML = '';
    document.body.classList.remove('tw-overflow-hidden');
    dirty = false;
    saving = false;

    if (opener && document.contains(opener)) {
        opener.focus();
    }
    opener = null;
}

function panelUrl(url) {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('panel', '1');

    return parsed.toString();
}

/** Show figures grouped as the full page does; they are unformatted again on save. */
function formatNumberFields(form) {
    const format = window.formatSevice?.thousandsFormat;
    if (typeof format !== 'function') return;

    form.querySelectorAll('.thousands-format').forEach((input) => {
        // A blank field stays blank rather than reading "0" that typing would append to.
        if (input.value.trim() === '') return;

        const decimals = input.dataset.decimals;
        format(window.$(input), decimals !== undefined ? decimals : '');
    });
}

/** Digits, one decimal separator and the grouping separator, as the full page allows. */
function allowFigureKey(event) {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;

    const { group, decimal } = window.InbudCurrency.separators();
    const isDigit = /^\d$/.test(event.key);
    const isDecimal = event.key === decimal && !event.target.value.includes(decimal);

    if (!isDigit && !isDecimal && event.key !== group) {
        event.preventDefault();
    }
}

/**
 * Regroup a whole figure as it is typed, in threes as the full page's fields are
 * (whatever the locale's own grouping), keeping the caret after the same digit.
 * Once a decimal separator is in, the field is left as typed, so trailing zeros
 * after it are not swallowed mid-entry.
 */
function regroupFigure(input) {
    const { group, decimal } = window.InbudCurrency.separators();
    if (input.value === '' || input.value.includes(decimal)) return;

    // A digit typed into a grouped figure leaves its groups uneven ("1,2500"),
    // so the separators are dropped before regrouping the whole number.
    const typed = input.value.split(group).join('');
    if (!/^\d+$/.test(typed)) return;
    // A leading zero would be grouped as a digit ("0,123").
    const digits = typed.replace(/^0+(?=\d)/, '');

    const caret = input.selectionStart ?? input.value.length;
    const strippedZeros = typed.length - digits.length;
    const digitsBeforeCaret = Math.max(input.value.slice(0, caret).split(group).join('').length - strippedZeros, 0);
    const regrouped = window.formatSevice.formatPrice(digits, 'any');
    if (regrouped === input.value) return;

    input.value = regrouped;

    let position = 0;
    for (let seen = 0; position < regrouped.length && seen < digitsBeforeCaret; position += 1) {
        if (regrouped[position] !== group) seen += 1;
    }
    input.setSelectionRange(position, position);
}

/** A figure field's value as a number, or NaN; reads any grouping the field may hold. */
function readFigure(input) {
    return window.formatSevice.parseFormattedMoney(input.value);
}

/**
 * A pasted figure is read as the full page reads one (either separator convention,
 * ambiguous text refused) and replaces the field's value; text that is not a figure
 * leaves the field as it was.
 */
function pasteFigure(event) {
    event.preventDefault();
    const input = event.currentTarget;
    const decimals = input.dataset.decimals;
    const pasted = (event.clipboardData || window.clipboardData).getData('text');
    const value = window.formatSevice.parsePastedMoney(pasted.trim(), decimals === undefined || decimals === 'any' ? Infinity : Number(decimals));
    if (!Number.isFinite(value)) return;

    input.value = window.formatSevice.formatPrice(value, 'any');
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

function bindNumberFields(form) {
    form.querySelectorAll('.thousands-format').forEach((input) => {
        input.addEventListener('keydown', allowFigureKey);
        input.addEventListener('input', () => regroupFigure(input));
        input.addEventListener('paste', pasteFigure);
    });
}

function figure(form, name) {
    const input = form.querySelector(`[name="${name}"]`);
    if (!input || input.value === '') return 0;

    const value = readFigure(input);

    return Number.isFinite(value) ? value : 0;
}

function inputFigure(input) {
    if (input.value === '') return 0;

    const value = readFigure(input);

    return Number.isFinite(value) ? value : 0;
}

/**
 * Keep each read-only total (Line revenue, Project value) in step with its fields:
 * the inputs named in data-fields, plus every input matching data-selector, which
 * covers rows added or removed after the panel opened.
 */
function bindTotals(form) {
    form.querySelectorAll('[data-side-panel-total]').forEach((total) => {
        const fields = (total.dataset.fields || '').split(',').filter(Boolean);
        const selector = total.dataset.selector || '';
        const output = total.querySelector('[data-side-panel-total-value]');
        const update = () => {
            const values = fields.map((name) => figure(form, name));
            if (selector) {
                form.querySelectorAll(selector).forEach((input) => values.push(inputFigure(input)));
            }
            const result =
                total.dataset.operation === 'multiply' ? values.reduce((product, value) => product * value, 1) : values.reduce((sum, value) => sum + value, 0);
            output.textContent = window.InbudCurrency.decimalUpTo(result + (parseFloat(total.dataset.base) || 0), 2);
        };

        form.addEventListener('input', update);
        update();
    });
}

/**
 * A new row's field that lists data-side-panel-row-candidates (JSON) starts on the
 * first candidate no other row already holds, so rows that must differ (a line's
 * units) do not open as duplicates.
 */
function fillRowDefaults(container, row) {
    row.querySelectorAll('[data-side-panel-row-candidates]').forEach((field) => {
        const name = field.dataset.sidePanelRowField;
        const taken = Array.from(container.querySelectorAll(`[data-side-panel-row] [data-side-panel-row-field="${name}"]`))
            .filter((other) => other !== field)
            .map((other) => other.value.trim().toLowerCase());
        let candidates = [];
        try {
            candidates = JSON.parse(field.dataset.sidePanelRowCandidates);
        } catch (error) {
            candidates = [];
        }

        field.value = candidates.find((candidate) => !taken.includes(String(candidate).trim().toLowerCase())) ?? '';
    });
}

/**
 * Repeatable rows (a line's quantities): each container holds rows cloned from its
 * template, named `{rows}[{index}][{field}]` and renumbered after every add or remove,
 * so the posted list never has gaps. Add hides at data-max rows, remove at one.
 */
function refreshRows(container) {
    const base = container.dataset.sidePanelRows;
    const rows = Array.from(container.querySelectorAll('[data-side-panel-row]'));
    const max = parseInt(container.dataset.max, 10) || Infinity;

    rows.forEach((row, index) => {
        row.querySelectorAll('[data-side-panel-row-field]').forEach((field) => {
            field.name = `${base}[${index}][${field.dataset.sidePanelRowField}]`;
        });
        row.querySelector('[data-side-panel-row-remove]')?.classList.toggle('tw-hidden', rows.length <= 1);
    });

    container.querySelector('[data-side-panel-row-add]')?.classList.toggle('tw-hidden', rows.length >= max);
}

function bindRows(form) {
    form.querySelectorAll('[data-side-panel-rows]').forEach((container) => {
        const list = container.querySelector('[data-side-panel-row-list]');
        const template = container.querySelector('template[data-side-panel-row-template]');

        container.addEventListener('click', (event) => {
            const add = event.target.closest('[data-side-panel-row-add]');
            const remove = event.target.closest('[data-side-panel-row-remove]');
            if (!add && !remove) return;

            event.preventDefault();
            let focusTarget = null;

            if (add && template && list) {
                const row = template.content.firstElementChild.cloneNode(true);
                list.appendChild(row);
                fillRowDefaults(container, row);
                bindNumberFields(row);
                focusTarget = row.querySelector('input');
            } else if (remove) {
                const row = remove.closest('[data-side-panel-row]');
                focusTarget = row?.previousElementSibling?.querySelector('input') || row?.nextElementSibling?.querySelector('input');
                row?.remove();
            }

            refreshRows(container);
            dirty = true;
            form.dispatchEvent(new Event('input', { bubbles: true }));
            focusTarget?.focus();
        });

        refreshRows(container);
    });
}

/**
 * The event-tags editor initialises once per page load. Without it the section
 * would post its "managed" marker with no rows and clear the line's tags, so the
 * marker is dropped and the tags are left as they are.
 */
function initEventTags(form) {
    if (!form.querySelector('#event-tags-section')) return;

    if (typeof window.initEventTagsSection === 'function') {
        window.initEventTagsSection();

        return;
    }

    form.querySelector('input[name="_event_tags_managed"]')?.remove();
}

function setSubtitle(text) {
    shell.subtitle.textContent = text;
    shell.subtitle.classList.toggle('tw-hidden', text === '');
}

/** A form that pairs its fields in two columns opens the panel wider. */
function setWide(wide) {
    shell.panel.classList.toggle('tw-max-w-xl', wide);
    shell.panel.classList.toggle('tw-max-w-md', !wide);
}

function mount(form) {
    shell.title.textContent = form.dataset.sidePanelTitle || '';
    setSubtitle(form.dataset.sidePanelSubtitle || '');
    setWide(form.hasAttribute('data-side-panel-wide'));
    formatNumberFields(form);
    bindNumberFields(form);
    bindRows(form);
    bindTotals(form);
    initEventTags(form);

    form.addEventListener('input', () => {
        dirty = true;
    });
    form.addEventListener('change', () => {
        dirty = true;
    });
    form.addEventListener('submit', submit);

    document.dispatchEvent(new CustomEvent('side-panel:mounted', { detail: { form } }));
    focusFirstField();
}

function open(url, trigger = null) {
    if (!url) return;

    shell = shell || buildShell();
    opener = trigger;
    dirty = false;
    saving = false;
    pendingHref = null;
    request += 1;
    const token = request;
    // A response for a panel since closed or replaced is dropped, and never navigates.
    const current = () => token === request && isOpen();

    shell.title.textContent = '';
    setSubtitle('');
    // A trigger can ask for the wide panel up front, so it does not widen once loaded.
    setWide(!!trigger && trigger.hasAttribute('data-side-panel-wide'));
    shell.body.innerHTML = `<div class="tw-p-6 tw-text-sm tw-text-charcoal-60">${t('Loading...')}</div>`;
    show();

    fetch(panelUrl(url), {
        headers: { Accept: 'text/html', 'X-Side-Panel': '1', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
    })
        .then((response) => (response.ok ? response.text() : Promise.reject(response)))
        .then((html) => {
            if (!current()) return;

            const holder = document.createElement('div');
            holder.innerHTML = html;
            const form = holder.querySelector('[data-side-panel-form]');

            if (!form) {
                window.location.href = url;

                return;
            }

            shell.body.innerHTML = '';
            shell.body.appendChild(form);
            mount(form);
        })
        .catch(() => {
            if (current()) window.location.href = url;
        });
}

function setSaving(form, value) {
    saving = value;
    const button = form.querySelector('[data-side-panel-submit]');
    if (button) button.disabled = value;
}

function clearErrors(form) {
    const box = form.querySelector('[data-side-panel-errors]');
    if (box) {
        box.innerHTML = '';
        box.classList.add('tw-hidden');
    }
    form.querySelectorAll('.is-invalid').forEach((field) => field.classList.remove('is-invalid'));
}

function showErrors(form, errors, message) {
    const box = form.querySelector('[data-side-panel-errors]');
    const messages = errors ? Object.values(errors).flat() : [message || t('Something went wrong. Please try again.')];

    if (errors) {
        Object.keys(errors).forEach((key) => {
            const name = key.includes('.') ? key.replace(/\.(\w+)/g, '[$1]') : key;
            form.querySelectorAll(`[name="${name}"]`).forEach((field) => field.classList.add('is-invalid'));
        });
    }

    if (box) {
        const list = document.createElement('ul');
        list.className = 'tw-mb-0';
        messages.forEach((text) => {
            const item = document.createElement('li');
            item.textContent = text;
            list.appendChild(item);
        });
        box.innerHTML = '';
        box.appendChild(list);
        box.classList.remove('tw-hidden');
        box.scrollIntoView({ block: 'nearest' });
    }
}

function formPayload(form) {
    const data = new FormData(form);

    // Figures go to the server canonical (no grouping, dot decimal) with every digit
    // kept, so a figure saved untouched is not rounded. Text that is not a figure is
    // sent as typed, so validation reports it.
    form.querySelectorAll('.thousands-format').forEach((input) => {
        if (!input.name || !data.has(input.name) || input.value === '') return;
        const canonicalText = window.formatSevice.canonicalFormattedMoney(input.value);
        if (canonicalText !== null) {
            data.set(input.name, canonicalText);
        }
    });

    return data;
}

function announce(json) {
    // An applied change the event logs (explanation_prompt) or a request queued for
    // approval (explanation_url) asks for an explanation now, as a page load would.
    const prompt = json.explanation_prompt || (json.pending_approval && json.explanation_url ? { url: json.explanation_url, reference: json.reference } : null);
    if (prompt && typeof window.promptForApprovalExplanation === 'function') {
        window.promptForApprovalExplanation(prompt);

        return;
    }

    if (json.message && typeof window.showToast === 'function') {
        window.showToast(json.message, json.pending_approval ? 'info' : 'success');
    }
}

function submit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (saving) return;

    clearErrors(form);
    setSaving(form, true);

    fetch(form.action, {
        method: 'POST',
        body: formPayload(form),
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
    })
        .then((response) =>
            response
                .json()
                .catch(() => ({}))
                .then((json) => ({ status: response.status, ok: response.ok, json })),
        )
        .then(({ status, ok, json }) => {
            if (!ok) {
                setSaving(form, false);
                showErrors(form, status === 422 ? json.errors : null, json.message);

                return;
            }

            const action = form.action;
            close();
            announce(json);
            document.dispatchEvent(new CustomEvent('side-panel:saved', { detail: { action, response: json } }));
        })
        .catch(() => {
            setSaving(form, false);
            showErrors(form, null, null);
        });
}

function onKeydown(event) {
    if (event.key !== 'Escape' || !isOpen()) return;
    // A dialog opened from inside the panel (the attachment upload) keeps Escape to itself.
    if (shell.body.querySelector('[aria-modal="true"]')) return;

    event.preventDefault();
    if (confirmShowing()) {
        hideConfirm();
    } else {
        requestClose();
    }
}

function onTriggerClick(event) {
    if (event.isDefaultPrevented() || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) return;

    event.preventDefault();
    if (this.classList.contains('disabled')) return;

    open(this.getAttribute('data-side-panel') || this.getAttribute('href'), this);
}

// Bound once jQuery is on the page (this module is evaluated before app.js sets it
// up), and through jQuery so a click the locked-budget confirmation replays with
// .click() after the user confirms still reaches it.
function bind() {
    window.$(document).on('click', '[data-side-panel]', onTriggerClick);
    document.addEventListener('keydown', onKeydown);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
} else {
    bind();
}

window.SidePanel = { open, close };
